import { ChargerAttributes } from "../../../models/charger-model";
import { UserPlanAssociation } from "../../../models/user-plan-associations-model";
import type { UserPricingOverride } from "../../../models/pricing-code-model";
import { generatePeakObj } from "./peak-off-peak-field";
import {
  isCurrentlyUnavailable,
  getNextUnavailableTransition,
} from "./unavailable-times-utils";

// A helper function to convert a 24-hour integer to a 12-hour string with AM/PM.
const formatHour = (hour: number) => {
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}${period}`;
};

const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const dayAbbreviations: Record<string, string> = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tues",
  Wednesday: "Wed",
  Thursday: "Thurs",
  Friday: "Fri",
  Saturday: "Sat",
};

interface PeakSchedule {
  start: number;
  end: number;
}

export const applyDriverPlanFees = (
  price: number,
  driverPlan: UserPlanAssociation | null
): number => {
  //this function returns the final price driver fees according to the driver plan
  if (driverPlan) {
    // console.log('driverPlan', driverPlan);
    // console.log('price', price);
    // console.log('kWh_markup', driverPlan?.driverPlan?.paymentFees?.kWh_markup);
    // console.log('discount', driverPlan?.driverPlan?.paymentFees?.discount);
    // console.log('pct_fee', driverPlan?.driverPlan?.paymentFees?.pct_fee);
    // console.log('final price', (price + (driverPlan?.driverPlan?.paymentFees?.kWh_markup ?? 0)) *
    //   (1 - (driverPlan?.driverPlan?.paymentFees?.discount ?? 0)) * // Apply discount
    //   (1 + (driverPlan?.driverPlan?.paymentFees?.pct_fee ?? 0)));
    return (
      (price + (driverPlan?.driverPlan?.paymentFees?.kWh_markup ?? 0)) *
      (1 - (driverPlan?.driverPlan?.paymentFees?.discount ?? 0)) * // Apply discount
      (1 + (driverPlan?.driverPlan?.paymentFees?.pct_fee ?? 0))
    ); // Apply fee
  } else {
    return price;
  }
};

/**
 * Mirrors the backend's `isDiscountEffectivelyFree` helper.
 * A discount is effectively free when:
 *  - discount_type is 'free', OR
 *  - discount_type is 'flat_rate' and the computed effective rates are both 0
 *    (i.e. the flat-rate discount >= the charger's base rates)
 */
export const isDiscountEffectivelyFree = (
  override?: UserPricingOverride | null
): boolean => {
  if (!override?.has_discount) return false;
  if (override.discount_type === 'free') return true;
  if (
    override.discount_type === 'flat_rate'
    && override.effective_price_peak === 0
    && override.effective_price_off_peak === 0
  ) return true;
  return false;
};

export const getBillingUnit = (charger: ChargerAttributes): string =>
  charger.billing_type === "hourly" ? "/hr" : "/kWh";

export const getEffectiveRates = (
  charger: ChargerAttributes,
  pricingOverride?: UserPricingOverride | null
) => {
  if (pricingOverride?.has_discount) {
    return {
      peak: pricingOverride.effective_price_peak,
      offPeak: pricingOverride.effective_price_off_peak,
    };
  }
  if (charger.billing_type === "hourly") {
    return {
      peak: charger.financials.price_hourly_peak ?? 0,
      offPeak: charger.financials.price_hourly_off_peak ?? 0,
    };
  }
  return {
    peak: charger.financials.price_kwh_peak,
    offPeak: charger.financials.price_kwh_off_peak,
  };
};

export const getPriceInfo = (
  charger: ChargerAttributes,
  driverPlan: UserPlanAssociation | null,
  pricingOverride?: UserPricingOverride | null
) => {
  const unit = getBillingUnit(charger);
  const peakData = generatePeakObj(charger.peak_selection);
  const { peak: basePeak, offPeak: baseOffPeak } = getEffectiveRates(charger, pricingOverride);

  const now = new Date();
  const currentHour = now.getHours();

  const todayName = days[now.getDay()];
  const todayPeak = peakData[todayName];

  let currentPrice;
  let priceLabel = "";
  let nextTransition = "";

  if (todayPeak && todayPeak.start !== todayPeak.end) {
    if (currentHour >= todayPeak.start && currentHour < todayPeak.end) {
      currentPrice = applyDriverPlanFees(basePeak, driverPlan);
      priceLabel = "pk";
      nextTransition = `next off-pk at ${formatHour(todayPeak.end)}`;
    } else {
      currentPrice = applyDriverPlanFees(baseOffPeak, driverPlan);
      priceLabel = "off-pk";
      if (currentHour < todayPeak.start) {
        nextTransition = `next pk at ${formatHour(todayPeak.start)}`;
      } else {
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        const tomorrowName = days[tomorrow.getDay()];
        const tomorrowPeak = peakData[tomorrowName];
        if (tomorrowPeak && tomorrowPeak.start !== tomorrowPeak.end) {
          nextTransition = `pk at ${formatHour(tomorrowPeak.start)} (tomorrow)`;
        }
      }
    }
  } else {
    currentPrice = applyDriverPlanFees(baseOffPeak, driverPlan);
  }

  const unavailable = isCurrentlyUnavailable(charger.unavailable_times ?? null);
  const unavailTransition = getNextUnavailableTransition(
    charger.unavailable_times ?? null,
    60
  );

  let unavailableInfo: string | null = null;
  if (unavailable && unavailTransition && !unavailTransition.becomesUnavailable) {
    unavailableInfo = `Available in ${unavailTransition.minutesUntil} min`;
  } else if (!unavailable && unavailTransition && unavailTransition.becomesUnavailable) {
    unavailableInfo = `Unavailable in ${unavailTransition.minutesUntil} min`;
  } else if (unavailable) {
    unavailableInfo = "Currently unavailable";
  }

  return {
    currentPrice,
    priceLabel,
    nextTransition,
    todayPeak,
    unit,
    isUnavailable: unavailable,
    unavailableInfo,
  };
};

/**
 * getRateTransitions
 *
 * This helper examines the current time and the provided peak schedule (peakData)
 * to return extra text for both the Peak and Off‑Peak rate rows.
 *
 * For each row:
 * - If the rate is currently active, it returns a string like "until 6PM".
 * - If it’s not active, it returns a string like "next at 5PM on Monday".
 *
 * Note that if no valid peak schedule exists for today (for example, Sunday with {start:0, end:0}),
 * then peak-related information will be empty.
 */
interface RateTransition {
  peakTransition: string;
  offPeakTransition: string;
  unavailableTransition: string;
}

export const getRateTransitions = (
  charger: ChargerAttributes
): RateTransition => {
  const peakData = generatePeakObj(charger?.peak_selection);

  const now = new Date();
  const currentHour = now.getHours();
  const todayName = days[now.getDay()];
  const todaySchedule = peakData[todayName];

  // Helper: Look for the next day (within the next 7 days) that has a valid peak schedule.
  const findNextValidSchedule = (
    date: Date
  ): { dayName: string; schedule: PeakSchedule } | null => {
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(date);
      nextDate.setDate(date.getDate() + i);
      const nextDayName = days[nextDate.getDay()];
      const schedule = peakData[nextDayName];
      if (schedule && schedule.start !== schedule.end) {
        return { dayName: nextDayName, schedule };
      }
    }
    return null;
  };

  let peakTransition = "";
  let offPeakTransition = "";

  // Determine if today's schedule is valid (e.g. Sunday might have {start: 0, end: 0})
  const validSchedule =
    todaySchedule && todaySchedule.start !== todaySchedule.end;

  // --- Peak rate transition info ---
  // Only calculate if a peak price is provided and today has a valid peak period.
  const { peak: peakRate, offPeak: offPeakRate } = getEffectiveRates(charger);

  if (peakRate && validSchedule) {
    if (currentHour < todaySchedule.start) {
      // Upcoming peak is later today.
      peakTransition = `next: ${dayAbbreviations[todayName]} at ${formatHour(
        todaySchedule.start
      )}`;
    } else if (
      currentHour >= todaySchedule.start &&
      currentHour < todaySchedule.end
    ) {
      // Peak is active.
      peakTransition = `until ${formatHour(todaySchedule.end)}`;
    } else {
      // Today's peak is over; look for the next valid schedule.
      const next = findNextValidSchedule(now);
      if (next) {
        peakTransition = `next: ${
          dayAbbreviations[next.dayName]
        } at ${formatHour(next.schedule.start)}`;
      }
    }
  }

  // --- Off-Peak rate transition info ---
  // Off-peak pricing is always provided. If there is a valid peak schedule,
  // then off-peak periods are defined by the start time of the next peak period.
  if (offPeakRate) {
    if (validSchedule) {
      if (currentHour < todaySchedule.start) {
        // Off-peak is active until peak starts later today.
        offPeakTransition = `until ${formatHour(todaySchedule.start)}`;
      } else if (
        currentHour >= todaySchedule.start &&
        currentHour < todaySchedule.end
      ) {
        // During peak hours, off-peak isn’t active, so show when it will resume.
        offPeakTransition = `next: ${
          dayAbbreviations[todayName]
        } at ${formatHour(todaySchedule.end)}`;
      } else {
        // After today's peak, off-peak is active until the next peak period.
        const next = findNextValidSchedule(now);
        // console.log("received next offpeak obj");
        // console.log(next);
        if (next) {
          offPeakTransition = `until ${formatHour(next.schedule.start)} on ${
            dayAbbreviations[next.dayName]
          }`;
        }
      }
    }
    // If there's no valid peak schedule, off-peak is always in effect and no extra text is needed.
  }

  let unavailableTransition = "";
  const unavailTimes = charger?.unavailable_times ?? null;
  if (unavailTimes) {
    const unavailNow = isCurrentlyUnavailable(unavailTimes);
    const transition = getNextUnavailableTransition(unavailTimes, 120);
    if (unavailNow && transition && !transition.becomesUnavailable) {
      unavailableTransition = `available at ${formatHour(transition.at.getHours())}`;
    } else if (!unavailNow && transition && transition.becomesUnavailable) {
      unavailableTransition = `unavailable at ${formatHour(transition.at.getHours())}`;
    } else if (unavailNow) {
      unavailableTransition = "currently unavailable";
    }
  }

  return {
    peakTransition,
    offPeakTransition,
    unavailableTransition,
  };
};
